import { Button } from '@appsumo/dorado-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { CSSProperties, useRef } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

const CountdownClockComponent = dynamic(
  () => import('@appsumo/dorado-react').then((mod) => mod.CountdownClock),
  { ssr: false },
);

const hasStarted = (end: string, start?: string): boolean => {
  const now = new Date();
  const endDate = new Date(end);

  if (start) {
    const startDate = new Date(start);
    if (now >= startDate && now < endDate) {
      return true;
    }
  } else if (now < endDate) {
    return true;
  }

  return false;
};

const hasEnded = (end: string): boolean => {
  const now = new Date();
  const endDate = new Date(end);
  return now >= endDate;
};

export function CountdownClock({
  ctaLink,
  ctaText,
  promoImage,
  backgroundImage,
  mobileBackgroundImage,
  startDate,
  endDate,
  more,
}: {
  ctaLink?: string;
  ctaText?: string;
  // Promotion image
  promoImage?: string;
  // Background image for promotion
  backgroundImage?: string;
  // Separate mobile background image
  mobileBackgroundImage?: string;
  /**
   * Start date of countdown clock
   */
  startDate?: string;
  /**
   * End date of countdown clock
   */
  endDate: string;
  /**
   * Link to the collection page
   */
  more: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const container = useIntersectionObserver(ref, { rootMargin: '150px 0px' });
  const isVisible = !!container?.isIntersecting;

  // Nothing to render if no dates are given
  if (!startDate && !endDate) return null;

  const started = hasStarted(endDate, startDate);
  const ended = hasEnded(endDate);
  const direction = started || ended ? 'Ends' : 'Starts';

  // Which time should we show
  let deadline: string | undefined = undefined;
  if (!started && !ended) {
    deadline = startDate;
  } else if (started || ended) {
    deadline = endDate;
  }

  const wrapperVariables = {
    '--background-image': `url(${backgroundImage})`,
    '--mobile-background-image': `url(${
      mobileBackgroundImage || backgroundImage
    })`,
  } as CSSProperties;

  const collectionButton = started && (
    <Button href={more}>View all deals</Button>
  );

  const ctaButton = ctaLink && ctaLink && (
    <Link
      className="rounded-full border-2 border-transparent bg-midnight px-6 py-2 font-header font-bold text-white hover:border-gravel
        hover:bg-gravel focus:border-black-pearl focus:bg-gravel active:border-black-pearl active:bg-black-pearl"
      href={ctaLink}
      target="_blank"
    >
      {ctaText}
    </Link>
  );

  return (
    <div
      ref={ref}
      className="bg-[image:var(--mobile-background-image)] bg-cover py-10 max-sm:mb-4 sm:pt-14 sm:pb-20 lg:bg-[image:var(--background-image)]"
      style={isVisible ? wrapperVariables : {}}
    >
      <div className="mx-auto w-fit">
        {promoImage && (
          <Image
            className="mx-auto"
            src={promoImage}
            alt="promotion logo"
            loading="lazy"
            height={100}
            width={440}
          />
        )}
        <div className="flex flex-col items-center">
          <span className="text-xl font-semibold">{direction} in</span>
          {/* Countdown clock */}
          {deadline ? (
            <CountdownClockComponent deadline={deadline} />
          ) : (
            <span>a bit!</span>
          )}
        </div>
        {/* Buttons */}
        <div className="mt-4 flex flex-row items-center justify-center gap-1">
          {collectionButton}
          {ctaButton}
        </div>
      </div>
    </div>
  );
}
